<template>
  <div :class="$setClasses">
    <div v-if="installmentSimulatedPlan">
      <div :class="$vClass('wrapper')">
        <p :class="$vClass('subtitle')" v-html="getTextVersion(planContent.subtitle)"></p>
        <div :class="$vClass('container')">
          <div v-for="installment in installmentSimulatedPlan.simulatedInstallments"
              :class="$vClass('item')"
              :key="installment.rank">
            <p :class="$vClass('item__before')">{{ getTextVersion(planContent.rank[installment.rank], {date: $helpers.demand.formatDate(installment.date)}) }}</p>
            <span class="u-dotted"></span>
            <p :class="$vClass('item__after')">{{ formatAmount(installment.amount) }} {{ $t('base.priceType', 2) }}
            </p>
            <span v-if="installment.rank > 1" :class="$vClass('item__schedule-rank')">{{  getTextVersion(planContent.scheduleRank[installment.rank])  }}</span>
          </div>
          <div v-if="getTextVersion(planContent.totalAmount)" :class="$vClass('item-total')">
            <p :class="$vClass('item-total__before')" v-html="getTextVersion(planContent.totalAmount)"></p>
            <span class="u-dotted"></span>
            <p :class="$vClass('item-total__after')">{{ `${formatAmount(installmentSimulatedPlan.customerTotalAmount)} ${$t('base.priceType', 2)}`  }}
            </p>
            <span :class="$vClass('item-total__schedule-rank')">
              {{ getTextVersion(planContent.fees, { amount: `${formatAmount(installmentSimulatedPlan.customerFees)} ${$t('base.priceType', 2)}` }) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useInstallmentStore, useDimensionStore } from '@/stores';
import formatHelpers from '@/utils/helpers/format';
import { useMoveManager, useMoveManagerEmits, useMoveManagerProps } from '@/composables/useMoveManager';
import offersHelper from '@/utils/helpers/offers';
import { i18n } from '@/plugins/i18n';
import { useConfigStore } from '@/stores/configStore';

defineOptions({
  name: 'InstallmentPlan',
  nameClassStyle: 'installment-plan',
});

const props = defineProps({
  ...useMoveManagerProps,
  optionsContent: {
    type: Object,
    default: () => ({}),
  },
  planContent: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(useMoveManagerEmits);

const configStore = useConfigStore();
const { buttonsDisabled } = storeToRefs(configStore);

const context = useMoveManager({ offer: props.offer }, { emit });
const { projectSelected, floaAlreadyPaid } = context;

const generatedPrice = computed(() => {
  return props.offer && offersHelper.getPrice(props.offer, !!context.exclTax.value);
});
const priceDiff = computed(() => {
  return Math.round((((generatedPrice.value ?? 0) + context.servicesPrice.value) - (context.ceiling.value || 0)) * 100) / 100;
});

const hasPaidArrhes = computed(() => {
  return props.offer?.arrhes_payment?.status === 'succeeded';
});

const textParams = computed(() => {
  const overrides: { arrhesAmount?: string; solde?: string } = {};
  if (hasPaidArrhes.value) {
    const arrhesAmount = context.arrhesAlreadyPaid.value?.amountReceived || props.offer?.arrhes_payment.amountReceived;
    overrides.arrhesAmount = i18n.global.n(arrhesAmount || 0, 'currency');
    overrides.solde = i18n.global.n(((context.calcPrice.value ?? 0) - (arrhesAmount ?? 0)) || 0, 'currency');
  }
  return {
    ...context.getTextParams.value,
    ...overrides,
    isMobile: isMobile.value,
    priceDiff: i18n.global.n(priceDiff.value || 0, 'currency'),
  };
});

const installmentStore = useInstallmentStore();
const dimensionStore = useDimensionStore();

const { installmentSimulatedPlans } = storeToRefs(installmentStore);

const isMobile = computed(() => dimensionStore.isMobile);
const installmentSimulatedPlan = computed(() => installmentStore.installmentSimulatedPlans[0]);

const getTextVersion = (text: string, params: Record<string, unknown> = {}) => {
  const paramsToMerge = {
    ...textParams.value,
    ...params,
  };
  return context.getTextVersion(text, { ...paramsToMerge });
};

const formatAmount = (amount: number) => formatHelpers.formatAmount(amount);

onMounted(async () => {
  try {
    if (!installmentSimulatedPlans.value.length) {
      buttonsDisabled.value = true;
      await installmentStore.getSimulateInstallmentPayment({
        token: projectSelected.value?.token ?? '',
        ...(floaAlreadyPaid.value?.dealReference ? { amount: floaAlreadyPaid.value?.amount } : {
          quoteId: props.offer?.id ?? '',
        })
      });
    }
  } catch (error) {
    console.error(error);
    buttonsDisabled.value = false;
  } finally {
    buttonsDisabled.value = false;
  }
});
</script>

<style lang="scss" scoped>
.installment-plan {
  width: 100%;
  height: 100%;
  min-height: 100px;

  &__subtitle {
    font-size: var(--nxt-font-size-small);

    @include breakpoint(tablet) {
      font-size: var(--nxt-font-size-default);
    }
  }

  &__item, &__item-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--nxt-font-size-default);
    position: relative;

    .u-dotted {
      display: none;
    }

    @include breakpoint(tablet) {
      font-size: var(--nxt-font-size-large);

      .u-dotted {
        display: flex;
      }
    }

    &__before {
      margin-right: var(--nxt-space-small);
    }

    &__after {
      margin-left: var(--nxt-space-small);
      text-align: end;
    }

    &__schedule-rank {
      display: none;

      @include breakpoint(tablet) {
        font-size: var(--nxt-font-size-small);
        display: block;
        position: absolute;
        bottom: -5px;
        right: 0;
      }
    }
  }

  &__item-total {
    &__before {
      font-weight: var(--nxt-font-weight-bold);
    }

    &__after {
      border-top: 1px solid var(--el-disabled-border-color);
    }
  }

  &__total-amount, &__fees {
    display: flex;
    justify-content: space-between;
    font-size: var(--nxt-font-size-default);
  }

  &__total-amount {
    padding-top: var(--nxt-space-default);
  }
}
</style>